import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';

/**
 * Auth slice managing user authentication state.
 */

function getStoredAuthToken() {
  return sessionStorage.getItem('auth_token') || null;
}

function persistAuthToken(token) {
  if (token) {
    sessionStorage.setItem('auth_token', token);
    return;
  }
  sessionStorage.removeItem('auth_token');
}

// Async: Register user
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/v1/auth/register', userData);
      return response.data;
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      const firstValidationMessage = validationErrors
        ? Object.values(validationErrors).flat()?.[0]
        : null;
      return rejectWithValue(
        firstValidationMessage
          || error.response?.data?.message
          || 'Registration failed'
      );
    }
  }
);

// Async: Login user
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      persistAuthToken(null);
      const response = await api.post('/v1/auth/login', {
        ...credentials,
        login_mode: 'web_session',
      });
      persistAuthToken(response.data?.token || null);
      return response.data;
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      const firstValidationMessage = validationErrors
        ? Object.values(validationErrors).flat()?.[0]
        : null;
      return rejectWithValue(
        firstValidationMessage
          || error.response?.data?.message
          || 'Login failed'
      );
    }
  }
);

// Async: Fetch current user
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/auth/user', { skipAuthRedirect: true });
      return response.data;
    } catch (error) {
      const storedToken = getStoredAuthToken();
      if (error.response?.status === 401 && storedToken) {
        try {
          const response = await api.get('/v1/auth/user', {
            headers: { Authorization: `Bearer ${storedToken}` },
            skipAuthRedirect: true,
          });
          return {
            ...response.data,
            token: storedToken,
          };
        } catch {
          persistAuthToken(null);
        }
      }
      return rejectWithValue('Session expired');
    }
  }
);

// Async: Logout
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/v1/auth/logout');
      persistAuthToken(null);
      return null;
    } catch (error) {
      persistAuthToken(null);
      return rejectWithValue('Logout failed');
    }
  }
);

export const stopImpersonation = createAsyncThunk(
  'auth/stopImpersonation',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/v1/auth/impersonation/stop');
      persistAuthToken(response.data?.token || null);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to stop impersonation'
      );
    }
  }
);

const initialState = {
  user: null,
  impersonation: null,
  token: getStoredAuthToken(),
  initialized: false,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        // Registration only creates account + sends verification email; user is not authenticated yet.
        state.user = null;
        state.token = null;
        state.impersonation = null;
        state.initialized = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token || 'session';
        state.impersonation = action.payload.impersonation || null;
        state.initialized = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch user
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload?.token || 'session';
        state.impersonation = action.payload?.impersonation || null;
        state.initialized = true;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.impersonation = null;
        state.token = null;
        state.initialized = true;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.impersonation = null;
        state.token = null;
        state.initialized = true;
      })
      // Stop impersonation
      .addCase(stopImpersonation.fulfilled, (state, action) => {
        state.user = action.payload?.user || state.user;
        state.impersonation = null;
        state.token = action.payload?.token || 'session';
        state.initialized = true;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
